import React, { useState, useEffect, useRef } from 'react';
import { authenticateAdmin, validateAdminSession } from '../../utils/api';
import AdminApis from '../../apis/admin';
import AdminLayout from './AdminLayout';
import AdminDashboard from './AdminDashboard';
import DealersManagement from './DealersManagement';
import AddDealer from './AddDealer';
import EditDealer from './EditDealer';
import ConvertCustomerToDealer from './ConvertCustomerToDealer';
import ManageProductVisibility from './ManageProductVisibility';

const AdminRoute = ({ authKey, uid }) => {
  const [currentView, setCurrentView] = useState('dashboard');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminData, setAdminData] = useState(null);
  const [adminApis, setAdminApis] = useState(null);
  const [error, setError] = useState(null);
  const [selectedDealer, setSelectedDealer] = useState(null);
  
  // Use ref to store the dealers refresh function
  const dealersRefreshRef = useRef(null);

  useEffect(() => {
    const validateAuth = async () => {
      try {
        setError(null);
        
        // First check if there's an existing session
        const sessionValidation = await validateAdminSession();
        
        if (sessionValidation.isValid) {
          console.log('Valid admin session found');
          console.log('sessionValidation.adminData', sessionValidation.adminData);
          setIsAuthenticated(true);
          setAdminData(sessionValidation.adminData);
          
          // Initialize admin APIs with OAuth data
          const storeId = sessionValidation.adminData.user?.storeId || sessionValidation.adminData.store?.id;
          const userEmail = sessionValidation.adminData.user?.storeEmail || sessionValidation.adminData.user?.email;
          
          console.log('Initializing AdminApis with storeId:', storeId, 'email:', userEmail);
          
          const apis = new AdminApis(
            storeId,
            "4", // extension_id
            userEmail
          );
          setAdminApis(apis);
          setLoading(false);
          return;
        }

        // If no valid session and we have credentials, try to authenticate
        if (authKey && uid) {
          const authResult = await authenticateAdmin(authKey, uid);
          
          console.log('authResult', authResult);
          if (authResult.success) {
            console.log('Admin authentication successful:', authResult);
            setIsAuthenticated(true);
            setAdminData(authResult.adminData);
            
            // Initialize admin APIs with OAuth data
            const storeId = authResult.adminData.user?.storeId || authResult.adminData.store?.id;
            const userEmail = authResult.adminData.user?.storeEmail || authResult.adminData.user?.email;
            
            console.log('Initializing AdminApis with storeId:', storeId, 'email:', userEmail);
            
            const apis = new AdminApis(
              storeId,
              "4", // extension_id  
              userEmail
            );
            setAdminApis(apis);
          } else {
            console.error('Admin authentication failed:', authResult.error);
            setError(authResult.error);
            setIsAuthenticated(false);
          }
        } else {
          console.log('No authentication credentials provided');
          setError('Authentication credentials (authKey and uid) are required in the URL');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Authentication error:', error);
        setError('An unexpected error occurred during authentication');
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    validateAuth();
  }, [authKey, uid]);

  // Register function for dealers refresh
  const registerDealersRefresh = (refreshFn) => {
    dealersRefreshRef.current = refreshFn;
  };

  // Handle dealer creation to refresh dealers list
  const handleDealerCreated = (newDealer) => {
    if (dealersRefreshRef.current) {
      dealersRefreshRef.current();
    }
  };

  // Custom setCurrentView function to handle dealer selection
  const handleSetCurrentView = (view, dealer = null) => {
    setCurrentView(view);
    setSelectedDealer(dealer);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="max-w-md w-full space-y-8 p-6">
          <div className="text-center">
            <div className="bg-red-100 w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01" />
              </svg>
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-4">
              Access Denied
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {error || 'You are not authorized to access the admin panel.'}
            </p>
            <div className="bg-gray-50 p-4 rounded-lg text-left">
              <h3 className="font-semibold text-gray-900 mb-2">How to access:</h3>
              <p className="text-xs text-gray-600 mb-2">
                Add the following parameters to your URL:
              </p>
              <code className="text-xs bg-gray-200 p-2 rounded block">
                ?authKey=your-key&uid=your-id
              </code>
              <p className="text-xs text-gray-500 mt-2">
                Example: /admin?authKey=admin123&uid=user123
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return <AdminDashboard adminApis={adminApis} adminData={adminData} setCurrentView={handleSetCurrentView} />;
      case 'dealers':
        return (
          <DealersManagement 
            adminApis={adminApis} 
            adminData={adminData} 
            setCurrentView={handleSetCurrentView}
            registerRefresh={registerDealersRefresh}
          />
        );
      case 'add-dealer':
        return (
          <AddDealer 
            adminApis={adminApis} 
            adminData={adminData} 
            onBack={() => setCurrentView('dealers')}
            onDealerCreated={handleDealerCreated}
          />
        );
      case 'edit-dealer':
        return (
          <EditDealer 
            adminApis={adminApis} 
            adminData={adminData} 
            dealer={selectedDealer}
            onBack={() => setCurrentView('dealers')}
            onDealerUpdated={handleDealerCreated}
          />
        );
      case 'convert-customer':
        return (
          <ConvertCustomerToDealer
            adminApis={adminApis}
            adminData={adminData}
            onBack={() => setCurrentView('dealers')}
            onDealerCreated={handleDealerCreated}
          />
        );
      case 'manage-visibility':
        return (
          <ManageProductVisibility
            adminApis={adminApis}
            adminData={adminData}
            dealer={selectedDealer}
            onBack={() => handleSetCurrentView('dealers')}
          />
        );
      default:
        return <AdminDashboard adminApis={adminApis} adminData={adminData} setCurrentView={handleSetCurrentView} />;
    }
  };

  return (
    <AdminLayout 
      currentView={currentView} 
      setCurrentView={setCurrentView}
      authKey={authKey}
      uid={uid}
      adminData={adminData}
    >
      {renderContent()}
    </AdminLayout>
  );
};

export default AdminRoute; 